import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';

import { getProgramEngagement } from '../../../../api/v2/program_insights';
import MetricCard from '../metric_card';
import KpiCards from '../kpi_cards';
import styles from './index.module.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const DESCRIPTION =
  'Monthly growth in unique participant engagement — total sign-ups per month split into pre-consent ' +
  '(recruitment) and post-consent (retention) mentor engagement. Depends on the sponsor Subject-ID feed ' +
  'to confirm the pre/post split.';

const SERIES = {
  total: '#183857',
  pre: '#419da5',
  post: '#72cbcf',
};

// Draws each bar's value above the column. chart.js has no built-in data labels,
// so this small plugin renders them after the bars are painted. All labels use the
// dark navy SERIES.total color for sufficient contrast on white.
const barValueLabels = {
  id: 'barValueLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;

      meta.data.forEach((bar, index) => {
        const value = dataset.data[index];
        if (value == null) return;

        ctx.save();
        ctx.fillStyle = SERIES.total;
        ctx.font = "700 10px 'Manrope', sans-serif";
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(value, bar.x, bar.y - 4);
        ctx.restore();
      });
    });
  },
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  layout: { padding: { top: 16 } },
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        boxWidth: 10,
        boxHeight: 10,
        usePointStyle: true,
        pointStyle: 'rectRounded',
        font: { size: 11.5, family: 'Manrope' },
        color: '#526d87',
      },
    },
    tooltip: {
      callbacks: {
        label: (context) => ` ${context.dataset.label}: ${context.formattedValue}`,
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { size: 11.5, family: 'Manrope' }, color: '#526d87' },
    },
    y: {
      beginAtZero: true,
      grid: { color: '#eef4f4' },
      border: { display: false },
      ticks: { precision: 0, font: { size: 11, family: 'Manrope' }, color: '#526d87' },
    },
  },
};

// Build a continuous range of months (inclusive) from the first to the last
// returned item. Uses the canonical `month` field (YYYY-MM). Missing months are
// added with zero counts.
const fillMissingMonths = (months, recruitmentAndRetention) => {
  if (!months || months.length === 0) return [];

  const sorted = [...months].sort((a, b) => (a.month < b.month ? -1 : a.month > b.month ? 1 : 0));
  const start = sorted[0].month; // YYYY-MM
  const end = sorted[sorted.length - 1].month; // YYYY-MM

  const [startYear, startMonth] = start.split('-').map((v) => parseInt(v, 10));
  const [endYear, endMonth] = end.split('-').map((v) => parseInt(v, 10));

  const monthKeyToItem = new Map(sorted.map((m) => [m.month, m]));
  const result = [];

  let y = startYear;
  let mIndex = startMonth - 1; // 0-based
  const totalSteps = (endYear - startYear) * 12 + (endMonth - startMonth);

  for (let step = 0; step <= totalSteps; step++) {
    const mm = String(mIndex + 1).padStart(2, '0');
    const key = `${y}-${mm}`;
    const existing = monthKeyToItem.get(key);
    if (existing) {
      result.push(existing);
    } else {
      result.push(
        recruitmentAndRetention ? { month: key, total: 0, pre_consent: 0, post_consent: 0 } : { month: key, total: 0 }
      );
    }
    // increment month
    mIndex += 1;
    if (mIndex >= 12) {
      mIndex = 0;
      y += 1;
    }
  }

  return result;
};

// Format "YYYY-MM" as "Mon YYYY" (e.g., "Jan 2025"). Always include year.
const formatMonthLabel = (yyyyMm) => {
  if (!yyyyMm) return '';
  const [y, m] = yyyyMm.split('-').map((v) => parseInt(v, 10));
  const d = new Date(y, (m || 1) - 1, 1);
  const mon = d.toLocaleString('en-US', { month: 'short' });
  return `${mon} ${d.getFullYear()}`;
};

const buildChartData = (data) => {
  const normalizedMonths = fillMissingMonths(data.months, data.recruitment_and_retention);
  const labels = normalizedMonths.map((m) => formatMonthLabel(m.month));
  const datasets = [
    {
      label: 'Total Unique Sign-Ups',
      data: normalizedMonths.map((month) => month.total),
      backgroundColor: SERIES.total,
      borderRadius: 4,
      maxBarThickness: 26,
    },
  ];

  if (data.recruitment_and_retention) {
    datasets.push({
      label: 'Pre-consent',
      data: normalizedMonths.map((month) => month.pre_consent),
      backgroundColor: SERIES.pre,
      borderRadius: 4,
      maxBarThickness: 26,
    });
    datasets.push({
      label: 'Post-consent',
      data: normalizedMonths.map((month) => month.post_consent),
      backgroundColor: SERIES.post,
      borderRadius: 4,
      maxBarThickness: 26,
    });
  }

  return { labels, datasets };
};

const buildKpis = (data) => {
  const cards = [{ label: 'Total Unique Participants', value: data.totals.total.toLocaleString() }];

  if (data.recruitment_and_retention) {
    cards.push({ label: 'Pre-consent', value: data.totals.pre_consent.toLocaleString() });
    cards.push({ label: 'Post-consent', value: data.totals.post_consent.toLocaleString() });
  }

  return cards;
};

export default function ProgramEngagement({ filters }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const controller = new AbortController();
    let isCurrent = true;

    setStatus('loading');

    getProgramEngagement(filters, { signal: controller.signal })
      .then((response) => {
        if (!isCurrent) return;
        setData(response);
        setStatus('loaded');
      })
      .catch((err) => {
        if (!isCurrent || err?.name === 'AbortError') return;
        setStatus('error');
      });

    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [filters]);

  const hasData = data && data.months.some((month) => month.total > 0);

  return (
    <MetricCard
      title='Program Engagement'
      subtitle='First mentor message before or after study consent.'
      description={DESCRIPTION}
      dependsOnFeed
    >
      {status === 'loading' && <p className={styles.state}>Loading…</p>}
      {status === 'error' && <p className={styles.stateError}>We could not load this metric. Try again shortly.</p>}

      {status === 'loaded' && !hasData && (
        <p className={styles.state}>No participant sign-ups match the current filters yet.</p>
      )}

      {status === 'loaded' && hasData && (
        <>
          <KpiCards cards={buildKpis(data)} />

          <div className={styles.chart} aria-label='Program Engagement monthly chart'>
            <Bar options={chartOptions} data={buildChartData(data)} plugins={[barValueLabels]} />
          </div>

          {/* Visually-hidden semantic table for screen readers */}
          <table className={styles.srOnly}>
            <caption>Program Engagement monthly values</caption>
            <thead>
              <tr>
                <th scope='col'>Month</th>
                <th scope='col'>Total Unique Sign-Ups</th>
                {data.recruitment_and_retention && <th scope='col'>Pre-consent</th>}
                {data.recruitment_and_retention && <th scope='col'>Post-consent</th>}
              </tr>
            </thead>
            <tbody>
              {fillMissingMonths(data.months, data.recruitment_and_retention).map((m) => (
                <tr key={m.month}>
                  <th scope='row'>{formatMonthLabel(m.month)}</th>
                  <td>{m.total}</td>
                  {data.recruitment_and_retention && <td>{m.pre_consent}</td>}
                  {data.recruitment_and_retention && <td>{m.post_consent}</td>}
                </tr>
              ))}
            </tbody>
          </table>

          <p className={styles.footnote}>
            {data.recruitment_and_retention ? (
              <>
                Monthly Total Unique Sign-Ups reconcile to the program total. Pre-consent and Post-consent cover people
                who messaged a mentor and can be less than the total. Counts are unique participants; zero values
                display as 0.
              </>
            ) : (
              <>
                Monthly totals reconcile to the program total. Counts are unique participants; zero values display as 0.
              </>
            )}
          </p>
        </>
      )}
    </MetricCard>
  );
}
